import { useState, type FormEvent } from "react";
import { accessRequestSchema } from "@atrium/shared";
import IconCheck from "~icons/solar/check-circle-bold";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { Spinner } from "../../components/ui/Spinner";

/**
 * Join the waitlist while Atrium is invite-only. The server answers the same
 * way for every address, so this always ends in "you're on the list".
 */
/** `glass` sits on the hero photo: translucent white, always light text. */
export function RequestInvite({ size = "md", tone = "default", className }: { size?: "md" | "lg"; tone?: "default" | "glass"; className?: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = accessRequestSchema.safeParse({ email });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message);
    setError(undefined);
    setState("sending");
    try {
      await api.post("/api/access/request", parsed.data);
      setState("done");
    } catch (err) {
      setError(errorMessage(err, "Couldn't add you to the list. Try again."));
      setState("idle");
    }
  };

  const lg = size === "lg";
  const glass = tone === "glass";
  if (state === "done")
    return (
      <div
        role="status"
        className={cn(
          "flex items-center gap-3 rounded-xl border text-left",
          glass ? "border-white/25 bg-white/12 backdrop-blur-md" : "border-accent/30 bg-accent-soft",
          lg ? "px-5 py-4" : "px-4 py-3",
          className,
        )}
      >
        <IconCheck className={cn("icon size-6 shrink-0", glass ? "text-white" : "text-accent")} aria-hidden />
        <div>
          <p className={cn("font-semibold", glass ? "text-white" : "text-fg")}>You're on the list</p>
          <p className={cn("text-sm", glass ? "text-white/75" : "text-fg-2")}>We'll email {email.trim().toLowerCase()} when your invite is ready.</p>
        </div>
      </div>
    );

  return (
    <form onSubmit={submit} noValidate className={cn("w-full", className)}>
      <div
        className={cn(
          "flex w-full items-center gap-1.5 rounded-xl border p-1.5 transition-colors",
          glass ? "bg-white/10 shadow-[0_20px_60px_-20px_rgb(0_0_40/0.6)] backdrop-blur-md focus-within:border-white/60" : "bg-surface/80 backdrop-blur focus-within:border-accent",
          error ? (glass ? "border-[#ffb4b4]" : "border-danger") : glass ? "border-white/25" : "border-line-strong",
        )}
      >
        <label htmlFor={`invite-email-${size}`} className="sr-only">
          Email address
        </label>
        <input
          id={`invite-email-${size}`}
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={!!error}
          aria-describedby={error ? `invite-error-${size}` : undefined}
          className={cn(
            "min-w-0 flex-1 bg-transparent px-3 focus:outline-none",
            glass ? "text-white placeholder:text-white/60" : "text-fg placeholder:text-fg-3",
            lg ? "h-11 text-base" : "h-9 text-sm",
          )}
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className={cn(
            "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:opacity-60",
            glass ? "bg-white text-[#0a1a5c] hover:bg-[#eef3ff]" : "bg-primary text-fg-inverse hover:bg-primary-hover",
            lg ? "h-11 px-5 text-base" : "h-9 px-4 text-sm",
          )}
        >
          {state === "sending" && <Spinner className="size-4" />}
          Request an invite
        </button>
      </div>
      {error && (
        <p id={`invite-error-${size}`} role="alert" className={cn("mt-2 text-left text-sm", glass ? "font-medium text-[#ffd0d0]" : "text-danger")}>
          {error}
        </p>
      )}
    </form>
  );
}
